import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import {
  apiPutPhysiciansUuid,
  PatientFacingPhysicianResponse,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { ErrorPage } from "../../../app/error-page";
import { useCountries } from "../../../catalogs/countries";
import { MonitoringUserBubbles } from "../../../components/monitoring-users/monitoring-user-bubbles";
import { monitoringUserFilter } from "../../../components/monitoring-users/monitoring-user-filter";
import { useMonitoringAssignments } from "../../../components/monitoring-users/use-monitoring-assignments";
import { Button } from "../../../components/ui-kit/button";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { hasMonitoringAccess } from "../../../constants/permissions";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { PhysiciansModal } from "../../admin/common/physicians-modal";
import { loadPhysicians } from "../../admin/physicians/physicians";
import { PatientSubpage } from "../common/patient-subpage";
import { PatientPhysicians } from "./patient-physicians";

export const PatientPhysiciansRoute = observer(
  function PatientPhysiciansRoute(props: { app: AppModel }) {
    const { app } = props;
    const { t } = useLingui();
    const patient = app.requirePatient();
    assert(patient.data, "Patient data must be loaded to view physicians");
    const countries = useCountries();

    const canAccess = hasMonitoringAccess(patient.api.permissions);
    const [editing, setEditing] = useState(false);
    const [model] = useState(
      () =>
        new PatientPhysicians(
          [],
          patient.api.permissions,
          patient.api.requireUserUUID(),
        ),
    );
    // The patient payload's physicians don't carry their monitoring users, so
    // the mapping is joined in from the physicians endpoint (same as the
    // patients list). Self-gates on monitoring access and swallows failures.
    const monitoringAssignments = useMonitoringAssignments(patient.api);

    useEffect(() => {
      if (canAccess && !app.adminShit) {
        void app.initAdmin();
      }
    }, [app, canAccess]);

    const physicians = useMemo(
      () => patient.data?.physicians ?? [],
      [patient.data.physicians],
    );
    const currentUuids = physicians.map((p) => p.uuid);
    const canEdit = app.adminShit?.initialized ?? false;

    useEffect(() => {
      model.updateData(physicians);
    }, [model, physicians]);

    if (!canAccess) {
      return (
        <ErrorPage
          title={t`Access denied`}
          message={t`You don't have permission to view physicians for this patient.`}
        />
      );
    }

    return (
      <PatientSubpage title={<Trans>Physicians</Trans>}>
        <DataTable
          model={model}
          itemKeyField="uuid"
          // Rebuilds the column defs when the assignments load, so the care
          // team filter's options aren't stuck on the pre-fetch (empty) set.
          filterDeps={[monitoringAssignments.byPhysician]}
          filterNoun={(count) =>
            t`${plural(count, {
              one: "physician assigned",
              other: "physicians assigned",
            })}`
          }
          filterActions={
            <Button
              disabled={!canEdit}
              onClick={() => setEditing(true)}
              title={canEdit ? undefined : t`Loading admin data, please wait…`}
            >
              <Trans>Edit physicians</Trans>
            </Button>
          }
          columns={[
            {
              header: <Trans>Family name</Trans>,
              attr: "family_name",
              type: "string",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
            },
            {
              header: <Trans>Given name</Trans>,
              attr: "given_name",
              type: "string",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
            },
            {
              header: <Trans>Country</Trans>,
              attr: "country",
              type: "enum",
              values: countries.countriesEnum,
              filter: {
                kind: "multi",
                group: t`Location`,
                options: getCountryOptions,
              },
            },
            {
              header: <Trans>Region</Trans>,
              attr: "region",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: (table) => getRegionOptions(table, countries),
                disabled: regionDisabled,
                disabledMessage: t`Select a country first`,
                predicate: regionPredicate,
              },
              cell: ({ item }) =>
                item.country && item.region != null
                  ? (countries.getRegionSchema(item.country, item.region)
                      ?.region_name ?? item.region)
                  : "—",
            },
            {
              header: <Trans>Monitoring users</Trans>,
              // The attr only provides the column id (a filter needs one);
              // the cell reads the joined assignments, not the field.
              attr: "assigned_monitoring_users",
              width: 120,
              filter: monitoringUserFilter<PatientFacingPhysicianResponse>({
                physiciansOf: (item) => [item],
                resolve: (physician) =>
                  monitoringAssignments.forPhysician(physician.uuid),
                kindOf: (uuid) => monitoringAssignments.findUser(uuid)?.kind,
              }),
              // Initials only — each bubble names its user and kind in
              // its own tooltip.
              cell: ({ item }) => {
                const users = monitoringAssignments.forPhysician(item.uuid);
                if (users.length === 0) return "—";
                return <MonitoringUserBubbles users={users} />;
              },
            },
            {
              header: <Trans>Email</Trans>,
              attr: "email",
              type: "string",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
            },
            {
              header: <Trans>Phone</Trans>,
              cell: ({ item }) => item.phone?.[0] ?? "—",
            },
            {
              header: <Trans>Clinics</Trans>,
              cell: ({ item }) => item.clinics?.length ?? 0,
            },
          ]}
        />

        {editing && canEdit && (
          <PatientPhysiciansEditModal
            app={app}
            patientName={patient.data.full_name ?? `#${patient.patientId}`}
            patientCountry={patient.data.country}
            currentUuids={currentUuids}
            onClose={() => setEditing(false)}
          />
        )}
      </PatientSubpage>
    );
  },
);

const PatientPhysiciansEditModal = observer(
  function PatientPhysiciansEditModal(props: {
    app: AppModel;
    patientName: string;
    patientCountry: string | null | undefined;
    currentUuids: string[];
    onClose: () => void;
  }) {
    const { app, patientName, patientCountry, currentUuids, onClose } = props;
    const { t } = useLingui();
    const [model] = useState(() => app.requireAdminShit().createPhysicians());
    const { alert } = useDialog();

    const handleConfirm = async ({ selected }: { selected: any[] }) => {
      const patient = app.requirePatient();
      const selectedSet = new Set<string>(selected as string[]);
      const currentSet = new Set(currentUuids);
      const toAppend = (selected as string[]).filter(
        (uuid) => !currentSet.has(uuid),
      );
      const toRemove = currentUuids.filter((uuid) => !selectedSet.has(uuid));
      if (toAppend.length === 0 && toRemove.length === 0) {
        return;
      }
      const patientId = patient.patientId;
      const requests = [
        ...toAppend.map((uuid) => ({
          uuid,
          body: { append_patients: [patientId] },
        })),
        ...toRemove.map((uuid) => ({
          uuid,
          body: { delete_patients: [patientId] },
        })),
      ];
      const results = await Promise.allSettled(
        requests.map(({ uuid, body }) =>
          apiPutPhysiciansUuid({ path: { uuid }, body }),
        ),
      );
      // Always refetch so the UI reflects whatever did succeed,
      // even if some PUTs failed.
      try {
        await patient.fetch();
      } catch {
        // surfaced below if there are also request failures; otherwise ignore
      }
      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        await alert(
          t`${failures} of ${plural(requests.length, {
            one: "# physician update",
            other: "# physician updates",
          })} failed. The list has been refreshed to reflect the current state.`,
          t`Failed to update physicians`,
        );
      }
    };

    return (
      <PhysiciansModal
        title={t`Edit physicians for patient: ${patientName}`}
        model={model}
        mode="multi"
        preselected={currentUuids}
        filter={patientCountry ? { country: [patientCountry] } : undefined}
        allowCreate
        showMonitoringUsers
        app={app}
        onAfterCreate={() => loadPhysicians(model)}
        onClose={onClose}
        onConfirm={handleConfirm}
      />
    );
  },
);
